<script>
import { getUserContract } from "../../api/user";
import DigitalSignCard from "./DigitalSignCard.vue";
import SignDialog from "./SignDialog.vue";
import WarningInfo from "../WarningInfo.vue";
import ManualSignDialog from "./ManualSignDialog.vue";
import SuccessMessage from "./SuccessMessage.vue";

export default {
  name: "DigitalSign",
  components: {SignDialog, DigitalSignCard, WarningInfo, ManualSignDialog, SuccessMessage},
  data() {
    return {
      type: "main",
      showDialog: false,
      sentType: '',
      initializing: true,
      fetchingContract: false,
      pdfUrl: null,
      checked: false,
      showManualDialog: false,
      showSuccess: false,
    }
  },
  computed: {
    contracts() {
      return this.$store.getters['user/contracts']
    },
    monetary() {
      return this.contracts.find(c => c.type === 'monetary')
    },
    main() {
      return this.contracts.find(c => c.type === 'main')
    },
    warningInfo() {
      return this.$t('warning.contract.warning')
    },
    signStatusDone() {
      return (
        this.main &&
        ['user_signed', 'admin_signed'].includes(this.main.status)
      )
    },
    isSignable() {
      return this.checked
    }
  },
  methods: {
    async fetchContract() {
      try {
        this.fetchingContract = true
        this.revokePdfUrl()
        const res = await getUserContract('main')
        const blob = new Blob([res.data], { type: 'application/pdf' })
        this.pdfUrl = URL.createObjectURL(blob)
      } catch (e) {
        console.error(e)
      } finally {
        this.fetchingContract = false
      }
    },
    revokePdfUrl() {
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl)
        this.pdfUrl = null
      }
    },
    signWithGSign() {
      this.type = "main"
      this.showDialog = true
    },
    signWithManual() {
      this.showManualDialog = true
    },

    manualSuccess() {
      this.showManualDialog = false
      this.$store.dispatch('user/fetchContracts')
      this.showSuccess = true
    },

    closeDialog() {
      this.showDialog = false
      this.$store.dispatch('user/fetchContracts')
    },
    success() {
      this.showDialog = false
      this.sentType = this.type
      this.$store.dispatch('user/fetchContracts')
      this.showSuccess = true
    },
    onSuccessDone() {
      this.showSuccess = false
    },
    nextStep() {
      this.$emit('selectTab', 5)
    }
  },
  async created() {
    try {
      if (!this.contracts || this.contracts.length === 0) {
        await this.$store.dispatch('user/fetchContracts')
      }
    } finally {
      this.initializing = false
    }
    // Гарын үсэг зурсан бол зөвхөн карт харагдана - гэрээний draft татах шаардлагагүй
    if (!this.signStatusDone) {
      this.fetchContract()
    }
  },
  beforeDestroy() {
    this.revokePdfUrl()
  }
}
</script>

<template>
  <div class="digital-sign-wrapper">
    <h5 class="contract-title">{{ $t('contract.brokerage') }}</h5>
    <SignDialog :type="type" :show="showDialog" @close="closeDialog" @success="success" :sentType="sentType"/>
    <ManualSignDialog :show="showManualDialog" @close="showManualDialog = false" @success="manualSuccess"/>
    <ic-dialog :show="showSuccess">
      <SuccessMessage
        :title="$t('contract.signed_successfully')"
        @done="onSuccessDone"
      />
    </ic-dialog>

    <!-- Гэрээний статус ирэх хүртэл аль нь ч харагдахгүй -->
    <div v-if="initializing" class="contract-section mb-4">
      <div class="contract-loading">{{ $t('contract.loading_agreement') }}</div>
    </div>

    <!-- Signed үед зөвхөн card харуулна -->
    <div v-else-if="signStatusDone" class="signed-card-wrap">
      <DigitalSignCard :contract="main"/>
    </div>

    <!-- Signed биш үед гэрээ унших + товчнууд -->
    <template v-else>
      <div class="contract-section mb-4">
        <div v-if="fetchingContract" class="contract-loading">
          {{ $t('contract.loading_agreement') }}
        </div>
        <div v-else class="contract-view">
          <embed
            v-if="pdfUrl"
            type="application/pdf"
            :src="pdfUrl"
          />
        </div>

        <transition name="fade">
          <label v-if="pdfUrl" class="contract-check" for="accept">
            <input type="checkbox" id="accept" v-model="checked">
            {{ $t('sign_dialog.agreement_read') }}
          </label>
        </transition>
      </div>

      <div class="contract-actions">
        <ic-button
          size="tn"
          :disabled="!isSignable"
          @click="signWithGSign"
          class="contract-buttons"
        >
          <img src="/img/gsign.png" alt="gSign" style="height: 25px; vertical-align: middle; margin-right: 6px;"/>
          {{ $t('kyc.sign_contract_by_gsign') }}
          <i class="sr sr-chevron-right" />
        </ic-button>
        <ic-button
          size="tn"
          :disabled="!isSignable"
          @click="signWithManual"
          class="contract-buttons"
        >
          <img src="/img/sign.png" alt="manual" style="height: 25px; vertical-align: middle; margin-right: 6px; background-color: white; border-radius: 4px; padding: 4px;"/>
          {{ $t('kyc.sign_contract_by_signature') }}
          <i class="sr sr-chevron-right" />
        </ic-button>
      </div>

      <div class="row mt-4">
        <div class="col">
          <warning-info :body="warningInfo" has-footer></warning-info>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.digital-sign-wrapper {
  max-width: 800px;
  margin: 0 auto;
}

.contract-title {
  text-align: center;
}

.title { font-size: 14px; font-weight: 500; }

.contract-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.contract-loading {
  padding: 20px 24px;
  color: var(--text-primary-color);
}

.contract-view {
  height: 800px;
  background: white;

  embed {
    width: 100%;
    height: 100%;
  }
}

.contract-check {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary-color);
  border-top: 1px solid #eee;
  cursor: pointer;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    cursor: pointer;
  }
}

.contract-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}
.contract-buttons {
  background: #0C293A !important
}
.signed-card-wrap {
  display: flex;
  justify-content: center;

  & > * {
    width: 100%;
    max-width: 480px;
  }
}

.fade-enter-active {
  transition: opacity 0.3s ease;
}
.fade-enter {
  opacity: 0;
}

</style>
